import React, { ForwardedRef, useState, useCallback, useRef } from 'react';
import {
  Box,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import {
  CheckCircle,
  ErrorOutline,
  ArrowForwardIos,
  ArrowBackIos,
  PersonOutline,
  BadgeOutlined,
  BusinessOutlined,
  CheckCircleOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import ServiceAPI from '../../services/serviceapi';
import { FormatUtils } from '../../utils/formatutils';

interface CadastroData {
  nome?: string;
  email?: string;
  url?: string;
  cpfCnpj?: string;
  celular?: string;
  cep?: string;
  responsavel?: string;
}

type StatusField = 'idle' | 'checking' | 'ok' | 'error';

interface VerificacaoStatus {
  status: StatusField;
  mensagem?: string;
  sugestoes?: string[];
}

const C = {
  marinho: '#1d2b4b',
  marinhoClaro: '#2a3d68',
  verde: '#82b07d',
  verdeEscuro: '#5e8f59',
  verdeAlpha: 'rgba(130,176,125,0.15)',
  branco: '#ffffff',
  cinzaFundo: '#f5f7fa',
  cinzaBorda: '#e1e7ef',
  cinzaTexto: '#64748b',
  erro: '#e05252',
};

const STEPS = [
  { label: 'Sobre você', icon: <PersonOutline sx={{ fontSize: 18 }} /> },
  { label: 'Sua empresa', icon: <BusinessOutlined sx={{ fontSize: 18 }} /> },
  { label: 'Confirmação', icon: <CheckCircleOutline sx={{ fontSize: 18 }} /> },
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: C.branco,
    fontSize: '14px',
    '& fieldset': { borderColor: C.cinzaBorda },
    '&:hover fieldset': { borderColor: C.verde },
    '&.Mui-focused fieldset': { borderColor: C.verde, borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: C.verde },
  '& .MuiFormHelperText-root': { fontSize: '11px', marginLeft: '2px' },
};

const Cadastro = React.forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [cadastro, setCadastro] = useState<CadastroData>({});
  const [docStatus, setDocStatus] = useState<VerificacaoStatus>({ status: 'idle' });
  const [urlStatus, setUrlStatus] = useState<VerificacaoStatus>({ status: 'idle' });
  const urlDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const set = (field: keyof CadastroData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setCadastro((p) => ({ ...p, [field]: e.target.value }));

  const verificarCpfCnpj = async (valor: string) => {
    const limpo = valor.replace(/\D/g, '');
    if (limpo.length < 11) { setDocStatus({ status: 'idle' }); return; }
    setDocStatus({ status: 'checking' });
    try {
      const result = await ServiceAPI.consumeService({
        url: '/public/verificar/cpfcnpj',
        data: limpo,
        config: { ignoreToast: true },
      });
      setDocStatus(
        result?.dataResponse?.disponivel
          ? { status: 'ok' }
          : { status: 'error', mensagem: 'CPF/CNPJ já cadastrado. Tente recuperar seu acesso.' }
      );
    } catch { setDocStatus({ status: 'idle' }); }
  };

  const verificarEmpresaId = useCallback(async (valor: string) => {
    if (valor.length < 3) { setUrlStatus({ status: 'idle' }); return; }
    setUrlStatus({ status: 'checking' });
    try {
      const result = await ServiceAPI.consumeService({
        url: '/public/verificar/empresaid',
        data: valor,
        config: { ignoreToast: true },
      });
      const data = result?.dataResponse;
      setUrlStatus(
        data?.disponivel
          ? { status: 'ok' }
          : { status: 'error', mensagem: 'Este ID já está em uso.', sugestoes: data?.sugestoes ?? [] }
      );
    } catch { setUrlStatus({ status: 'idle' }); }
  }, []);

  const onChangeUrl = (value: string) => {
    const clean = value.replace(/[^a-z0-9]/g, '').toLowerCase();
    setCadastro((p) => ({ ...p, url: clean }));
    setUrlStatus({ status: 'idle' });
    if (urlDebounceRef.current) clearTimeout(urlDebounceRef.current);
    urlDebounceRef.current = setTimeout(() => verificarEmpresaId(clean), 600);
  };

  const canProceedStep0 =
    !!cadastro.nome &&
    !!cadastro.email &&
    !!cadastro.cpfCnpj &&
    docStatus.status !== 'error' &&
    docStatus.status !== 'checking';

  const canProceedStep1 =
    !!cadastro.url &&
    urlStatus.status === 'ok';

  const cadastrar = async () => {
    setEnviando(true);
    try {
      const result = await ServiceAPI.consumeService({
        url: '/public/cadastro/cliente',
        data: cadastro,
        config: { ignoreToast: false },
      });
      if (result.codeResponse === 'SUCESSO_PERSISTENCIA') {
        toast.success('Sucesso! Confira seu e-mail.');
        navigate('/confirmacaocadastro');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Falha ao cadastrar');
    } finally {
      setEnviando(false);
    }
  };

  const adornment = (s: VerificacaoStatus) => {
    if (s.status === 'checking') return <CircularProgress size={16} sx={{ color: C.verde }} />;
    if (s.status === 'ok') return <CheckCircle sx={{ color: C.verde, fontSize: 18 }} />;
    if (s.status === 'error') return <ErrorOutline sx={{ color: C.erro, fontSize: 18 }} />;
    return null;
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── Painel esquerdo ── */}
      <Box
        sx={{
          width: { xs: 0, md: '42%' },
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          bgcolor: C.marinho,
          p: '48px 52px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* padrão decorativo */}
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.04,
          backgroundImage: `radial-gradient(circle, ${C.verde} 1px, transparent 1px)`,
          backgroundSize: '28px 28px',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -120, right: -80,
          width: 400, height: 400, borderRadius: '50%',
          background: `radial-gradient(circle, ${C.verde}33 0%, transparent 70%)`,
        }} />

        <Box sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 6 }}>
            <img src="/AppCodesh.png" alt="App Codesh" style={{ height: 36, filter: 'brightness(0) invert(1)' }} />
          </Box>

          <Typography sx={{ fontSize: 32, fontWeight: 700, color: C.branco, lineHeight: 1.25, mb: 2 }}>
            Gerencie seu negócio com inteligência
          </Typography>
          <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
            Crie sua conta gratuitamente e tenha acesso à plataforma completa de gestão.
          </Typography>

          <Stack spacing={3} sx={{ mt: 6 }}>
            {[
              { icon: '⚡', titulo: 'Ativação imediata', desc: 'Seu ambiente fica pronto em segundos após a confirmação' },
              { icon: '🔒', titulo: 'Dados protegidos', desc: 'Infraestrutura isolada por empresa com schema dedicado' },
              { icon: '📱', titulo: 'Suporte via WhatsApp', desc: 'Notificações e suporte direto no seu celular' },
            ].map((item) => (
              <Box key={item.titulo} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '10px',
                  bgcolor: C.verdeAlpha, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 18, flexShrink: 0,
                }}>
                  {item.icon}
                </Box>
                <Box>
                  <Typography sx={{ color: C.branco, fontWeight: 600, fontSize: 14 }}>{item.titulo}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, mt: 0.3 }}>{item.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, position: 'relative' }}>
          © 2025 AppCodesh · Todos os direitos reservados
        </Typography>
      </Box>

      {/* ── Painel direito ── */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: C.cinzaFundo, p: { xs: '32px 20px', md: '48px 40px' },
        overflowY: 'auto',
      }}>

        {/* logo mobile */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 4, justifyContent: 'center' }}>
          <img src="/AppCodesh.png" alt="App Codesh" style={{ height: 32 }} />
        </Box>

        <Box sx={{ width: '100%', maxWidth: 460 }}>

          {/* Steps */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 5 }}>
            {STEPS.map((s, i) => (
              <React.Fragment key={i}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: i < step ? C.verde : i === step ? C.marinho : C.cinzaBorda,
                    color: i <= step ? C.branco : C.cinzaTexto,
                    transition: 'all 0.35s ease',
                    fontSize: 14, fontWeight: 600,
                  }}>
                    {i < step ? <CheckCircle sx={{ fontSize: 18 }} /> : s.icon}
                  </Box>
                  <Typography sx={{
                    fontSize: 11, fontWeight: i === step ? 600 : 400,
                    color: i === step ? C.marinho : C.cinzaTexto,
                    whiteSpace: 'nowrap',
                  }}>
                    {s.label}
                  </Typography>
                </Box>
                {i < STEPS.length - 1 && (
                  <Box sx={{
                    flex: 1, height: '2px', mx: 1, mb: 2.5,
                    bgcolor: i < step ? C.verde : C.cinzaBorda,
                    transition: 'background 0.4s ease',
                  }} />
                )}
              </React.Fragment>
            ))}
          </Box>

          {/* Card */}
          <Box sx={{
            bgcolor: C.branco, borderRadius: '20px',
            border: `1px solid ${C.cinzaBorda}`,
            boxShadow: '0 4px 24px rgba(29,43,75,0.07)',
            p: { xs: '28px 24px', md: '36px 40px' },
          }}>

            {/* ─── Step 0: Sobre você ─── */}
            {step === 0 && (
              <Stack spacing={2.5}>
                <Box sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.marinho }}>Sobre você</Typography>
                  <Typography sx={{ fontSize: 13, color: C.cinzaTexto, mt: 0.5 }}>Dados do responsável pela conta</Typography>
                </Box>

                <TextField
                  label="Nome completo"
                  size="small"
                  value={cadastro.nome || ''}
                  onChange={set('nome')}
                  fullWidth
                  sx={inputSx}
                />

                <TextField
                  label="Email"
                  size="small"
                  type="email"
                  value={cadastro.email || ''}
                  onChange={set('email')}
                  fullWidth
                  sx={inputSx}
                />

                <Box>
                  <TextField
                    label="CPF ou CNPJ"
                    size="small"
                    value={cadastro.cpfCnpj || ''}
                    onChange={(e) => {
                      setCadastro((p) => ({ ...p, cpfCnpj: FormatUtils.formatarCpfCnpj(e.target.value) }));
                      setDocStatus({ status: 'idle' });
                    }}
                    onBlur={() => cadastro.cpfCnpj && verificarCpfCnpj(cadastro.cpfCnpj)}
                    error={docStatus.status === 'error'}
                    helperText={docStatus.status === 'error' ? docStatus.mensagem : undefined}
                    fullWidth
                    InputProps={{ endAdornment: <InputAdornment position="end">{adornment(docStatus)}</InputAdornment> }}
                    sx={inputSx}
                  />
                </Box>

                <TextField
                  label="WhatsApp"
                  size="small"
                  value={cadastro.celular || ''}
                  onChange={(e) => setCadastro((p) => ({ ...p, celular: FormatUtils.formatarNumeroCelular(e.target.value) }))}
                  fullWidth
                  sx={inputSx}
                />

                <Button
                  fullWidth
                  disabled={!canProceedStep0}
                  onClick={() => setStep(1)}
                  endIcon={<ArrowForwardIos sx={{ fontSize: 12 }} />}
                  sx={{
                    mt: 1, py: 1.4, borderRadius: '10px', fontWeight: 700,
                    textTransform: 'none', fontSize: 14,
                    bgcolor: canProceedStep0 ? C.marinho : C.cinzaBorda,
                    color: canProceedStep0 ? C.branco : C.cinzaTexto,
                    boxShadow: canProceedStep0 ? '0 4px 14px rgba(29,43,75,0.25)' : 'none',
                    transition: 'all 0.25s',
                    '&:hover': { bgcolor: canProceedStep0 ? C.marinhoClaro : C.cinzaBorda },
                    '&.Mui-disabled': { bgcolor: C.cinzaBorda, color: C.cinzaTexto },
                  }}
                >
                  Continuar
                </Button>
              </Stack>
            )}

            {/* ─── Step 1: Empresa ─── */}
            {step === 1 && (
              <Stack spacing={2.5}>
                <Box sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.marinho }}>Sua empresa</Typography>
                  <Typography sx={{ fontSize: 13, color: C.cinzaTexto, mt: 0.5 }}>Configure seu ambiente na plataforma</Typography>
                </Box>

                <Box>
                  <Box sx={{
                    display: 'flex', alignItems: 'center',
                    border: `1px solid ${urlStatus.status === 'error' ? C.erro : urlStatus.status === 'ok' ? C.verde : C.cinzaBorda}`,
                    borderRadius: '10px', overflow: 'hidden', bgcolor: C.branco,
                    transition: 'border-color 0.2s',
                    '&:focus-within': { borderColor: C.verde, outline: `1.5px solid ${C.verde}` },
                  }}>
                    <Box sx={{ px: 1.5, py: 1.1, bgcolor: '#f8fafc', borderRight: `1px solid ${C.cinzaBorda}`, whiteSpace: 'nowrap' }}>
                      <Typography sx={{ fontSize: 11, color: C.cinzaTexto }}>gestao.appcodesh.com.br/</Typography>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 1.5 }}>
                      <input
                        placeholder="minha-empresa"
                        value={cadastro.url || ''}
                        onChange={(e) => onChangeUrl(e.target.value)}
                        style={{
                          border: 'none', outline: 'none', width: '100%',
                          fontSize: 14, color: C.marinho, background: 'transparent',
                          fontFamily: 'inherit', padding: '9px 0',
                        }}
                      />
                      <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>{adornment(urlStatus)}</Box>
                    </Box>
                  </Box>

                  {urlStatus.status === 'ok' && (
                    <Typography sx={{ fontSize: 11, color: C.verde, mt: 0.5, ml: 0.5 }}>
                      ✓ Disponível — gestao.appcodesh.com.br/{cadastro.url}
                    </Typography>
                  )}
                  {urlStatus.status === 'error' && (
                    <Typography sx={{ fontSize: 11, color: C.erro, mt: 0.5, ml: 0.5 }}>
                      {urlStatus.mensagem}
                    </Typography>
                  )}
                  {urlStatus.status === 'idle' && (
                    <Typography sx={{ fontSize: 11, color: C.cinzaTexto, mt: 0.5, ml: 0.5 }}>
                      Somente letras minúsculas e números. Não pode ser alterado depois.
                    </Typography>
                  )}

                  {urlStatus.status === 'error' && (urlStatus.sugestoes?.length ?? 0) > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography sx={{ fontSize: 11, color: C.cinzaTexto, mb: 0.75 }}>Sugestões disponíveis:</Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.75}>
                        {urlStatus.sugestoes!.map((s) => (
                          <Chip
                            key={s} label={s} size="small" clickable
                            onClick={() => { setCadastro((p) => ({ ...p, url: s })); setUrlStatus({ status: 'ok' }); }}
                            sx={{
                              bgcolor: C.verdeAlpha, color: C.marinho, fontWeight: 600,
                              fontSize: 11, border: `1px solid ${C.verde}44`,
                              '&:hover': { bgcolor: `${C.verde}30` },
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>

                <TextField
                  label="CEP"
                  size="small"
                  value={cadastro.cep || ''}
                  onChange={(e) => setCadastro((p) => ({ ...p, cep: FormatUtils.formatarCEP(e.target.value) }))}
                  fullWidth
                  sx={inputSx}
                />

                <Stack direction="row" spacing={1.5}>
                  <Button
                    fullWidth
                    onClick={() => setStep(0)}
                    startIcon={<ArrowBackIos sx={{ fontSize: 11 }} />}
                    sx={{
                      py: 1.4, borderRadius: '10px', fontWeight: 600, textTransform: 'none',
                      fontSize: 14, border: `1px solid ${C.cinzaBorda}`, color: C.cinzaTexto,
                      bgcolor: 'transparent', '&:hover': { bgcolor: C.cinzaFundo },
                    }}
                  >
                    Voltar
                  </Button>
                  <Button
                    fullWidth
                    disabled={!canProceedStep1}
                    onClick={() => setStep(2)}
                    endIcon={<ArrowForwardIos sx={{ fontSize: 12 }} />}
                    sx={{
                      py: 1.4, borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: 14,
                      bgcolor: canProceedStep1 ? C.marinho : C.cinzaBorda,
                      color: canProceedStep1 ? C.branco : C.cinzaTexto,
                      boxShadow: canProceedStep1 ? '0 4px 14px rgba(29,43,75,0.25)' : 'none',
                      '&:hover': { bgcolor: canProceedStep1 ? C.marinhoClaro : C.cinzaBorda },
                      '&.Mui-disabled': { bgcolor: C.cinzaBorda, color: C.cinzaTexto },
                    }}
                  >
                    Continuar
                  </Button>
                </Stack>
              </Stack>
            )}

            {/* ─── Step 2: Revisar ─── */}
            {step === 2 && (
              <Stack spacing={2.5}>
                <Box sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.marinho }}>Tudo certo?</Typography>
                  <Typography sx={{ fontSize: 13, color: C.cinzaTexto, mt: 0.5 }}>Revise antes de criar sua conta</Typography>
                </Box>

                <Box sx={{ borderRadius: '12px', border: `1px solid ${C.cinzaBorda}`, overflow: 'hidden' }}>
                  {[
                    { label: 'Nome', valor: cadastro.nome },
                    { label: 'Email', valor: cadastro.email },
                    { label: 'CPF / CNPJ', valor: cadastro.cpfCnpj },
                    { label: 'WhatsApp', valor: cadastro.celular || '—' },
                    { label: 'ID da empresa', valor: `gestao.appcodesh.com.br/${cadastro.url}` },
                    { label: 'CEP', valor: cadastro.cep || '—' },
                  ].map((row, idx, arr) => (
                    <Box key={row.label} sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      px: 2, py: 1.5,
                      borderBottom: idx < arr.length - 1 ? `1px solid ${C.cinzaBorda}` : 'none',
                      bgcolor: idx % 2 === 0 ? C.branco : '#fafbfc',
                    }}>
                      <Typography sx={{ fontSize: 12, color: C.cinzaTexto }}>{row.label}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.marinho, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>
                        {row.valor}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{
                  display: 'flex', gap: 1.5, alignItems: 'flex-start',
                  bgcolor: C.verdeAlpha, borderRadius: '10px', p: '12px 14px',
                }}>
                  <Typography sx={{ fontSize: 18 }}>🔒</Typography>
                  <Typography sx={{ fontSize: 12, color: C.marinho, lineHeight: 1.6 }}>
                    Seus dados são protegidos. Após clicar em criar conta, você receberá um e-mail de confirmação.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    fullWidth
                    onClick={() => setStep(1)}
                    startIcon={<ArrowBackIos sx={{ fontSize: 11 }} />}
                    sx={{
                      py: 1.4, borderRadius: '10px', fontWeight: 600, textTransform: 'none',
                      fontSize: 14, border: `1px solid ${C.cinzaBorda}`, color: C.cinzaTexto,
                      bgcolor: 'transparent', '&:hover': { bgcolor: C.cinzaFundo },
                    }}
                  >
                    Editar
                  </Button>
                  <Button
                    fullWidth
                    disabled={enviando}
                    onClick={cadastrar}
                    sx={{
                      py: 1.4, borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: 14,
                      bgcolor: C.verde, color: C.branco,
                      boxShadow: '0 4px 14px rgba(130,176,125,0.4)',
                      '&:hover': { bgcolor: C.verdeEscuro },
                      '&.Mui-disabled': { bgcolor: C.cinzaBorda, color: C.cinzaTexto },
                    }}
                  >
                    {enviando ? <CircularProgress size={18} sx={{ color: C.branco }} /> : 'Criar minha conta 🚀'}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>

          <Typography sx={{ mt: 3, textAlign: 'center', fontSize: 13, color: C.cinzaTexto }}>
            Já tem conta?{' '}
            <Box
              component="span"
              onClick={() => navigate('/login')}
              sx={{ color: C.marinho, fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              Fazer login
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

export default Cadastro;